import * as braze from '@braze/web-sdk'

import { User } from 'common/core/interfaces/user'
import { handleCatchError } from 'utils/handleCatchError'
import { analyticsService } from 'utils/services/analytics'

import { EngagementServiceEvents } from './constants'
import { EngagementServiceInterface } from './types'

let pauseId = 1

class BrazeEngagementService implements EngagementServiceInterface {
  #isInitialized = false

  /**
   * Used to manage multiple simultaneous invocations of `engagementService.pauseEngagement` correctly.
   */
  private engagementPauses = new Set<number>()

  private inAppMessagesSubscription: string | undefined = undefined

  public init(userId: User['user_id']) {
    try {
      if (this.isInitialized) {
        return
      }

      const BrazeAppId = window.__INIT__?.['app.braze.appId']
      const BrazeSDKURL = window.__INIT__?.['app.braze.sdkUrl']
      if (!BrazeAppId || !BrazeSDKURL) {
        throw new Error('Braze init error: missing API config')
      }

      if (typeof userId !== 'number') {
        throw new Error('Braze init error: missing user id')
      }

      braze.initialize(BrazeAppId, {
        baseUrl: BrazeSDKURL,
        allowUserSuppliedJavascript: true,
      })

      braze.changeUser(String(userId))

      this.inAppMessagesSubscription = braze.subscribeToInAppMessage(this.inAppMessagesListener)

      braze.openSession()

      this.isInitialized = true
      analyticsService.sendEvent(EngagementServiceEvents.init.success)
    } catch (error) {
      handleCatchError(error)
      analyticsService.sendEvent(EngagementServiceEvents.init.failure)
    }
  }

  public logEvent = (eventName: string, eventProperties: object): void => {
    if (!this.isInitialized) {
      return
    }

    braze.logCustomEvent(eventName, eventProperties)
  }

  public pauseEngagement = () => {
    const pause = ++pauseId
    this.engagementPauses.add(pause)

    analyticsService.sendEvent(EngagementServiceEvents.pause.started)

    return () => {
      this.engagementPauses.delete(pause)
      analyticsService.sendEvent(EngagementServiceEvents.pause.ended)

      this.onReleaseEngagement()
    }
  }

  public dispose = () => {
    if (!this.isInitialized) {
      return
    }

    if (this.inAppMessagesSubscription) {
      braze.removeSubscription(this.inAppMessagesSubscription)
    }

    if (braze.isInitialized()) {
      braze.destroy()
    }

    this.isInitialized = false
    analyticsService.sendEvent(EngagementServiceEvents.dispose.called)
  }

  public get isPaused() {
    return this.engagementPauses.size > 0
  }

  public get isInitialized() {
    return this.#isInitialized
  }

  private set isInitialized(value: boolean) {
    this.#isInitialized = value
  }

  private onReleaseEngagement = () => {
    if (!this.isInitialized || this.isPaused) {
      // still having 1+ active pauses
      return
    }

    const deferredInAppMessage = braze.getDeferredInAppMessage()
    if (deferredInAppMessage) {
      this.inAppMessagesListener(deferredInAppMessage)
    }
  }

  private inAppMessagesListener = (inAppMessage: braze.InAppMessage | braze.ControlMessage) => {
    if (!this.isInitialized) {
      return
    }

    analyticsService.sendEvent(EngagementServiceEvents.inAppMessage.received)

    // control group messages should always be "shown"
    // this will log an impression and not show a visible message
    if (inAppMessage instanceof braze.ControlMessage) {
      braze.showInAppMessage(inAppMessage)

      analyticsService.sendEvent(EngagementServiceEvents.inAppMessage.shownControl)
      return
    }

    if (!this.isInitialized || this.isPaused) {
      braze.deferInAppMessage(inAppMessage)

      analyticsService.sendEvent(EngagementServiceEvents.inAppMessage.deferred)
      return
    }

    this.showInAppMessage(inAppMessage)
  }

  private showInAppMessage(inAppMessage: braze.InAppMessage) {
    braze.showInAppMessage(inAppMessage)

    analyticsService.sendEvent(EngagementServiceEvents.inAppMessage.shown)
  }
}

export const engagementService: EngagementServiceInterface = new BrazeEngagementService()

window.engagement = engagementService
